import i18n from '@/i18n'
import { useValidator } from '@/validation/form-validator'
import { useClientStore } from '@/stores/client'
import { isClientSectionHidden } from '@/helpers/client-feature-flags'

// Form value types for validators - using unknown for maximum flexibility
type FormValue = unknown
type ValidatorFn = (value: FormValue) => boolean | string

// Helper to get typed translation - cast to avoid vue-i18n type complexity
const t = (key: string, params?: Record<string, number | string>): string => {
  if (params) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (i18n.global as any).t(key, params)
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (i18n.global as any).t(key)
}

export const countriesWithLocation = ['CH']

export const requiredValue: ValidatorFn = (value) => {
  if (!value || value === '') {
    return t('form.error.required')
  }
  return true
}

export const requiredValueInCountry: ValidatorFn = (value) => {
  // TEMPORARY: Skip validation for clients with hidden location section
  const clientStore = useClientStore()
  const clientName = clientStore.getUser?.name
  if (isClientSectionHidden(clientName, 'location')) {
    return true
  }

  const validator = useValidator()
  const country = validator.getValue('country')

  if (typeof country === 'string' && countriesWithLocation.includes(country)) {
    if (!value || value === '') {
      return t('form.error.required')
    }
  }
  return true
}

export const minArrayLength = (minLength: number = 1): ValidatorFn => {
  return (value) => {
    if (!value || (typeof value === 'object' && Object.keys(value).length < minLength)) {
      return t('form.error.min_length', { minLength })
    }
    return true
  }
}

export const requiredMinValue = (minValue: number): ValidatorFn => {
  return (value) => {
    if (typeof value === 'number' && value < minValue) {
      return t('form.error.min_value', { number: minValue })
    }
    if (value === null || value === '') {
      return t('form.error.required')
    }
    return true
  }
}

export const nonRequiredMinValue = (minValue: number): ValidatorFn => {
  return (value) => {
    if (!value && value !== 0) {
      return true
    }
    if (typeof value === 'number' && value < minValue) {
      return t('form.error.min_value', { number: minValue })
    }
    return true
  }
}

export const nonRequiredMaxValue = (maxValue: number): ValidatorFn => {
  return (value) => {
    if (!value && value !== 0) {
      return true
    }
    if (typeof value === 'number' && value > maxValue) {
      return t('form.error.max_value', { number: maxValue })
    }
    return true
  }
}

export const equalTo = (fieldPath: string): ValidatorFn => {
  if (fieldPath.includes('[]')) {
    throw new Error('Validator [equalTo] is not supported for nested arrays')
  }
  return (value) => {
    const validator = useValidator()
    const otherValue = validator.getValue(fieldPath)
    if (value && otherValue !== value) {
      if (fieldPath === 'user.email' || fieldPath === 'email') {
        return t('form.error.equal_to_email_confirm')
      } else {
        return t('form.error.equal_to')
      }
    }
    return true
  }
}

export const extend = (message: string, validatorFn: ValidatorFn): ValidatorFn => {
  return (value) => {
    const result = validatorFn(value)
    if (result !== true) {
      return t(message)
    }
    return true
  }
}
